'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSearchParams } from 'next/navigation';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { useRouter } from "next/navigation";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, MoreHorizontal } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import React from 'react';
import LoadingGif from '@/../public/loading.gif';
import { IBillingTransaction } from '@/lib/interfaces';
import { TDraftElementsCategory } from '@/data/types';
import { transactionsColumns } from '@/lib/tables-defs';
import LoginRedirect from '@/components/LoginRedirect';
import { getBillingTransactions, getCurrentPlan } from './actions';
import HttpStatus from '@/lib/HttpStatus';
import { addMonths, format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { cancelSubscription, isFreePlan } from '@/app/plans/actions';
import { ToastError, ToastMessage } from '@/lib/sonnerUtils';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';

function BillingQuota() {
  const searchParams = useSearchParams();
    const [ transactions, setTransactions ] = useState<IBillingTransaction[]>([]);
    const router = useRouter();

    const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
    const [ userDisplayName, setUserDisplayName ] = useState<string>('');
    const [rowSelection, setRowSelection] = React.useState({});
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [ currentPlan, setCurrentPlan ] = useState<string>('');
    const [ currPlanPrice, setCurrPlanPrice ] = useState<string>('');
    const [ lastPaymentDate, setLastPaymentDate ] = useState<string>('');
    const [ lastPaymentStatus, setLastPaymentStatus ] = useState<string>('');
    const [ nextDuePayment, setNextDuePayment ] = useState<string>('');
    const [ isFreeUser, setIsFreeUser ] = useState<boolean>(true);
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>({});
  
    const table = useReactTable({
        data: transactions,
        columns: transactionsColumns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
          sorting,
          columnFilters,
          columnVisibility,
          rowSelection,
        },
      })    
  
  useEffect(() => {
    const fetchTransactions = async (desData: { userId: string, count: number }) => {
    //   const transactions: IBillingTransaction[] = await fetchAndCreateTransactionsCards(desData, TDraftElementsCategory.FULL) as IBillingTransaction[];
    //   setTransactions(prev => ([...transactions]));

      try {
        const planNameResp = await getCurrentPlan(user?.id!);
        const transResp = await getBillingTransactions(user?.id!);
        const isFreeResp = await isFreePlan(user?.id!);

        if(planNameResp.status === HttpStatus.OK && planNameResp.data.length > 0) {
            setCurrentPlan(planNameResp.data[0].planName);
            setCurrPlanPrice(planNameResp.data[0].planPrice);
            setLastPaymentDate(planNameResp.data[0].paymentDate || 'N/A');
            setLastPaymentStatus(planNameResp.data[0].paymentStatus || 'N/A');

            if(planNameResp.data[0].paymentDate) {
                const lpd = new Date(planNameResp.data[0].paymentDate);
                const nextDate = addMonths(lpd, 1);
                setNextDuePayment(format(nextDate, 'MMMM do, yyyy'));
            } else setNextDuePayment('N/A');
        }
        else throw new Error(planNameResp.error!);

        console.log(transResp.data);

        if(transResp.status === HttpStatus.OK) {
            setTransactions(transResp.data);
        }
        else throw new Error(planNameResp.error!);

        if(isFreeResp.status == HttpStatus.OK) setIsFreeUser(isFreeResp.data);
        else setIsFreeUser(true);

      } catch(error) {
        console.error('Error', error);
        setCurrentPlan('N/A');
      }
    }

    if (!isLoading && isAuthenticated) {
      setUserDisplayName(user?.email ?? 'No found');
    //   setUserId(user!.id);
      const desData = { userId: user!.id, count: 0 };
      fetchTransactions(desData);
      setDataFetched(true);
    } else if(!isLoading && !isAuthenticated) {
        router.push("/api/auth/login");
    }
  }, [user, isLoading, isAuthenticated])
  
    const [isMyLoading, setIsLoading] = useState(false);
    const [ dataFetched, setDataFetched ] = useState<boolean>(false);
  
    const handleCancelSubscription = async () => {
        const cancelResp = await cancelSubscription(user?.id!);

        if(cancelResp.success) ToastMessage(cancelResp.message!);
        else ToastError(cancelResp.error);
    }

  return (
    <>
        <div className="space-y-10 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                My Billing & Quota
            </h1>

            <div className='w-full flex justify-center items-center'>
                <AdSterraNativeBanner />
            </div>

            <div className='w-full'>
                <Tabs defaultValue='billing' className='w-full text-2xl'>
                    <TabsList>
                        <TabsTrigger value="billing" 
                            className='w-[200px] text-xl'>
                                Billing
                        </TabsTrigger>
                        <TabsTrigger value="transactions" 
                            className='w-[200px] text-xl'>
                                Transactions
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="billing" className='w-full'>
                        <div className="w-full">
                            {/* -------------------- Input Form Card -------------------- */}
                            <Card className="shadow-xl dark:bg-gray-800">
                            <CardHeader className="border-b dark:border-gray-700">
                                <CardTitle className="text-xl text-indigo-600">Current Billing Cycle</CardTitle>
                                {/* <CardDescription>Define the relationship and what you need to communicate.</CardDescription> */}
                            </CardHeader>
                            <CardContent className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <div className='flex flex-row gap-4'>
                                        <div className='ml-4'> 
                                            <Label className='text-indigo-600 text-xl'>Current Plan:</Label>
                                        </div>
                                        <div className='mr-4'>
                                            <Label className='text-black text-xl font-medium'>{currentPlan}</Label>
                                        </div>
                                    </div>
                                    <div className='flex flex-row gap-4'>
                                        <div className='ml-4'> 
                                            <Label className='text-indigo-600 text-xl'>Plan Price:</Label>
                                        </div>
                                        <div className='mr-4'>
                                            <Label className='text-black text-xl font-medium'>{`$${currPlanPrice}`}</Label>
                                        </div>
                                    </div>
                                    <div className='flex flex-row gap-4'>
                                        <div className='ml-4'> 
                                            <Label className='text-indigo-600 text-xl'>Last Payment Status:</Label>
                                        </div>
                                        <div className='mr-4'>
                                            <Label className='text-black text-xl font-medium'>
                                                {
                                                    lastPaymentStatus.toUpperCase() == 'COMPLETE' || lastPaymentStatus.toUpperCase() == 'N/A' 
                                                    ? <span className='text-green-600'>{lastPaymentStatus.toUpperCase()}</span>
                                                    : <span className='text-red-600'>{lastPaymentStatus.toUpperCase()}</span>
                                                }
                                            </Label>
                                        </div>
                                    </div>
                                    <div className='flex flex-row gap-4'>
                                        <div className='ml-4'> 
                                            <Label className='text-indigo-600 text-xl'>Due Date:</Label>
                                        </div>
                                        <div className='mr-4'>
                                            <Label className='text-black text-xl font-medium'>{nextDuePayment}</Label>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter
                                className="border-t pt-4 dark:border-gray-700"
                            >        
                                <div >
                                    <Button variant={"destructive"} 
                                        disabled={isFreeUser}
                                        onClick={handleCancelSubscription}
                                    >
                                        Cancel Subscription
                                    </Button>
                                </div>
                            </CardFooter>
                            </Card>        
                        </div>
                    </TabsContent>
                    <TabsContent value="transactions">
                        <div className="overflow-hidden rounded-md border">
                            <Table>
                                <TableHeader className='bg-indigo-400 text-white'>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                            <TableHead key={header.id} className='text-white'>
                                                {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </TableHead>
                                            )
                                        })}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                {table.getRowModel().rows?.length == 0 && !dataFetched ? (
                                    <TableRow>
                                    <TableCell
                                        colSpan={transactionsColumns.length}
                                        className="h-24 text-center"
                                    >
                                        <div className='w-full text-center flex flex-row justify-center items-center'>
                                        {isLoading ?
                                            <Image src={LoadingGif} alt={'Loading Data'} width={40} /> :
                                            <span>No Results</span>
                                        }

                                        </div>
                                    </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row, i) => (
                                    <TableRow className={i % 2 == 1 ? 'bg-indigo-100' : ''}
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return <TableCell key={cell.id}>
                                                {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                                )}
                                            </TableCell>
                                        })}
                                    </TableRow>
                                    ))
                                )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>

        <div className='w-full flex justify-center items-center mt-4'>
            <AdSterraIFrameBannerAuto position='bottom' />
        </div>

        {/* 2. THE OVERLAY (Only shows if not logged in) */}
            {(!isAuthenticated && !isLoading) && (
            <LoginRedirect message="to analyze this job description" />
        )}
    </>
  );
}

export default BillingQuota